import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, shareReplay } from 'rxjs';
import { environment } from 'projects/ai_modernization/src/environments/environment';
import { Privilege, AccessType, Role } from '../../models/authorization';
import { AccessTypeMapper } from '../../utils/access-type-mapper';
import { ConfigService } from './config.service';

type PrivilegeDto = { permissionKey: string; access: string | AccessType | null | undefined };
type RolePrivilegesResponse = { rolePrivileges: PrivilegeDto[]; roles: string[] };

@Injectable({
  providedIn: 'root'
})
export class UserPrivilegesService {
  private readonly userPrivilegesEndpoint = '/authorization';
  private cachedRolePrivilegesResponse$?: Observable<RolePrivilegesResponse>;

  constructor(
    private http: HttpClient,
    private configService: ConfigService
  ) { }

  /**
   * Returns current user's merged privileges.
   * - In mock mode: loaded from JSON file (authorization.mockUserPrivilegesPath)
   * - Otherwise: fetched from backend (/api/authorization)
   */
  public getUserPrivileges(): Observable<Privilege[]> {
    return this.getRolePrivilegesResponse().pipe(
      map((payload) => this.normalizePrivilegesPayload(payload))
    );
  }

  /**
   * Returns current user's roles.
   * - In mock mode: loaded from JSON file (authorization.mockUserPrivilegesPath)
   * - Otherwise: fetched from backend (/api/authorization)
   */
  public getUserRoles(): Observable<Role[]> {
    return this.getRolePrivilegesResponse().pipe(
      map((payload) => this.normalizeRolesPayload(payload))
    );
  }

  private getRolePrivilegesResponse(): Observable<RolePrivilegesResponse> {
    const authConfig = this.configService.getAuthorizationConfig();

    if (authConfig.useMockPrivileges === true) {
      if (!this.cachedRolePrivilegesResponse$) {
        const path = authConfig.mockUserPrivilegesPath ?? 'assets/mock/user-privileges-admin.json';
        this.cachedRolePrivilegesResponse$ = this.http.get<RolePrivilegesResponse>(path).pipe(
          shareReplay(1)
        );
      }
      return this.cachedRolePrivilegesResponse$;
    }

    if (!this.cachedRolePrivilegesResponse$) {
      const url = this.resolveUrl(this.userPrivilegesEndpoint);
      this.cachedRolePrivilegesResponse$ = this.http.get<RolePrivilegesResponse>(url).pipe(
        shareReplay(1)
      );
    }
    return this.cachedRolePrivilegesResponse$;
  }

  private normalizePrivilegesPayload(payload: RolePrivilegesResponse): Privilege[] {
    return payload.rolePrivileges
      .map((p) => ({
        permissionKey: p.permissionKey,
        access: AccessTypeMapper.fromLegacy(p.access ?? null)
      }))
      .filter((p) => !!p.permissionKey)
      .filter((p) => p.access !== AccessType.None);
  }

  private normalizeRolesPayload(payload: RolePrivilegesResponse): Role[] {
    const matched = (payload.roles || [])
      .map((roleName) => roleName as Role)
      .filter((role) => Object.values(Role).includes(role));
    
    if (matched.length === 0) {
      return [Role.PlatformUser];
    }
    
    if (matched.length === 1 && matched[0] === Role.Admin) {
      return [Role.Admin, Role.PlatformUser];
    }
    
    return matched;
  }

  private resolveUrl(urlOrPath: string): string {
    const value = (urlOrPath ?? '').trim();
    if (!value) {
      return environment.apiUrl + '/authorization';
    }

    if (/^https?:\/\//i.test(value)) {
      return value;
    }

    if (value.startsWith('/')) {
      return environment.apiUrl + value;
    }

    return environment.apiUrl + '/' + value;
  }
}
